import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import type { OARankingRow, OARankingWidget } from '@/types/oa-ranking'

export interface OARankingCardProps {
  widget: OARankingWidget
  rows: OARankingRow[]
  isLoading: boolean
  isError: boolean
  error: Error | null
  onRetry: () => void
}

const MAX_ROWS = 10

function RankBadge({ rank }: { rank: number }) {
  let rankClass = 'bg-muted text-muted-foreground'

  if (rank === 1) {
    rankClass = 'bg-yellow-100 text-yellow-700 dark:bg-yellow-500/20 dark:text-yellow-400'
  } else if (rank === 2) {
    rankClass = 'bg-slate-100 text-slate-700 dark:bg-slate-500/20 dark:text-slate-400'
  } else if (rank === 3) {
    rankClass = 'bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-400'
  }

  return (
    <span
      className={`inline-flex h-5 w-12 items-center justify-center rounded text-xs font-semibold ${rankClass}`}
    >
      NO.{rank}
    </span>
  )
}

function LoadingRows() {
  return Array.from({ length: MAX_ROWS }).map((_, index) => (
    <div key={index} className="flex items-center justify-between py-2">
      <div className="flex items-center gap-3">
        <Skeleton className="h-5 w-12" />
        <Skeleton className="h-5 w-32" />
      </div>
      <Skeleton className="h-5 w-16" />
    </div>
  ))
}

export function OARankingCard({
  widget,
  rows,
  isLoading,
  isError,
  error,
  onRetry,
}: OARankingCardProps) {
  const showTotal = widget.metric !== 'performance'

  return (
    <Card className="flex h-full flex-col">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold">{widget.title}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-1 flex-col">
        {isLoading ? (
          <LoadingRows />
        ) : isError ? (
          <div className="flex flex-1 flex-col items-center justify-center gap-3 py-8 text-center">
            <p className="text-sm text-muted-foreground">
              加载失败{error ? `：${error.message}` : ''}
            </p>
            <Button variant="outline" size="sm" onClick={onRetry}>
              重试
            </Button>
          </div>
        ) : rows.length === 0 ? (
          <div className="flex flex-1 items-center justify-center py-8 text-sm text-muted-foreground">
            暂无数据
          </div>
        ) : (
          <div className="flex flex-col">
            {rows.slice(0, MAX_ROWS).map((row) => (
              <div
                key={row.id}
                className="flex items-center justify-between border-b border-border/50 py-2 last:border-b-0"
              >
                <div className="flex min-w-0 items-center gap-3">
                  <RankBadge rank={row.rank} />
                  <span className="truncate text-sm">{row.name}</span>
                </div>
                {showTotal && row.total != null && (
                  <span className="shrink-0 text-sm font-medium tabular-nums">
                    {row.total}
                  </span>
                )}
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
